import React from 'react';

export interface CatalogColor {
  id: number;
  code: string;
  name: string;
  hexColor?: string | null;
}

const FALLBACK_COLOR_OPTIONS = [
  'CIAN', 'MAGENTA', 'AMARILLO', 'NEGRO', 'BLANCO',
  'NARANJA', 'ROJO', 'VERDE', 'AZUL', 'VIOLETA',
  'CAFÉ', 'PLATA', 'ORO', 'TRANSPARENTE',
];

export const COLOR_ROW_COUNT = 9;

// Fixed-size color grid: keeps saved rows in place, pads missing ones. The form ignores any
// other length (renders blank), so every place that loads/copies specs must go through this.
export const normalizeColorRows = (rows: unknown): Array<{ colorBase: string; pantone: string }> =>
  Array.from({ length: COLOR_ROW_COUNT }, (_, i) => {
    const r = Array.isArray(rows) ? (rows as any[])[i] : undefined;
    return { colorBase: r?.colorBase ?? '', pantone: r?.pantone ?? '' };
  });

interface ProcessSpecsTableFormProps {
  specs: any;
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
  catalogColors?: CatalogColor[];
  standPouchEnabled?: boolean;
}

const num = (v: any) => parseFloat(String(v ?? ''));

const calc = (a: any, b: any, op: 'mul' | 'add') => {
  const av = num(a), bv = num(b);
  if (isNaN(av) || isNaN(bv) || av === 0 || bv === 0) return '';
  const r = op === 'mul' ? av * bv : av + bv;
  return Number.isInteger(r) ? String(r) : r.toFixed(2);
};

const ProcessSpecsTableForm: React.FC<ProcessSpecsTableFormProps> = ({ specs, onChange, disabled, catalogColors, standPouchEnabled }) => {
  const colorOptions = (catalogColors && catalogColors.length > 0)
    ? catalogColors.filter(c => c.name).map(c => c.name)
    : FALLBACK_COLOR_OPTIONS;
  const s = specs || {};
  const colorRows: Array<{ colorBase: string; pantone: string }> =
    Array.isArray(s.colorRows) && s.colorRows.length === COLOR_ROW_COUNT ? s.colorRows : normalizeColorRows(s.colorRows);

  const mangaCount = parseInt(String(s.cylinderCount ?? '0')) || 0;

  const printWidth = calc(s.individualWidth, s.bobinCount, 'mul');
  const mangaCircComputed = calc(s.individualRepetition, s.repetitionCount, 'mul');

  // ROT-164: when the order's manufacturing process has "Stand Pouch" enabled,
  // ANCHO DEL CORTE P/BOLSEO and ANCHO DEL SUSTRATO are derived from
  // ANCHO DE IMPRESIÓN + REFILE P/BOLSA STAND POUCH instead of the default formula.
  const baggingCutWidthStandPouch = standPouchEnabled
    ? calc(printWidth || s.printWidth, s.refileStandPouch, 'add')
    : '';
  const baggingCutWidth = standPouchEnabled ? baggingCutWidthStandPouch : s.baggingCutWidth;
  const substrateWidth = standPouchEnabled
    ? calc(baggingCutWidthStandPouch || s.baggingCutWidth, s.refileStandPouch, 'add')
    : calc(printWidth || s.printWidth, s.reelWidth, 'add');

  const handleA = (v: string) => {
    onChange('individualWidth', v);
    const c = calc(v, s.bobinCount, 'mul');
    onChange('printWidth', c);
    if (standPouchEnabled) {
      const bagging = calc(c || s.printWidth, s.refileStandPouch, 'add');
      onChange('baggingCutWidth', bagging);
      onChange('substrateWidth', calc(bagging || s.baggingCutWidth, s.refileStandPouch, 'add'));
    } else {
      onChange('substrateWidth', calc(c || s.printWidth, s.reelWidth, 'add'));
    }
  };
  const handleB = (v: string) => {
    onChange('bobinCount', v);
    const c = calc(s.individualWidth, v, 'mul');
    onChange('printWidth', c);
    if (standPouchEnabled) {
      const bagging = calc(c || s.printWidth, s.refileStandPouch, 'add');
      onChange('baggingCutWidth', bagging);
      onChange('substrateWidth', calc(bagging || s.baggingCutWidth, s.refileStandPouch, 'add'));
    } else {
      onChange('substrateWidth', calc(c || s.printWidth, s.reelWidth, 'add'));
    }
  };
  const handleD = (v: string) => {
    onChange('reelWidth', v);
    onChange('substrateWidth', calc(printWidth || s.printWidth, v, 'add'));
  };
  const handleRefileStandPouch = (v: string) => {
    onChange('refileStandPouch', v);
    const bagging = calc(printWidth || s.printWidth, v, 'add');
    onChange('baggingCutWidth', bagging);
    onChange('substrateWidth', calc(bagging || s.baggingCutWidth, v, 'add'));
  };
  const handleF = (v: string) => {
    onChange('individualRepetition', v);
    onChange('cylinderCircumference', calc(v, s.repetitionCount, 'mul'));
  };
  const handleG = (v: string) => {
    onChange('repetitionCount', v);
    onChange('cylinderCircumference', calc(s.individualRepetition, v, 'mul'));
  };

  const handleColorRow = (idx: number, field: 'colorBase' | 'pantone', value: string) => {
    const updated = colorRows.map((row, i) => i === idx ? { ...row, [field]: value } : row);
    onChange('colorRows', updated);
  };

  const inputCls = 'w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400';
  const labelCls = 'border px-3 py-2 font-medium text-gray-700 text-xs bg-gray-50 whitespace-nowrap';
  const valueCls = 'border px-2 py-1';
  const unitCls = 'border px-2 py-1 text-gray-400 text-xs text-center';
  const computedCls = 'border px-3 py-2 bg-green-50 text-green-700 font-semibold text-center text-sm';

  return (
    <div>
      <table className="w-full border border-gray-300 text-sm table-fixed">
        <colgroup>
          <col className="w-[22%]" />
          <col className="w-[16%]" />
          <col className="w-[7%]" />
          <col className="w-[22%]" />
          <col className="w-[26%]" />
          <col className="w-[7%]" />
        </colgroup>
        <tbody>
          {/* Row A | F */}
          <tr>
            <td className={labelCls}>ANCHO INDIVIDUAL:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.individualWidth ?? ''} disabled={disabled}
                onChange={e => handleA(e.target.value)} />
            </td>
            <td className={unitCls}>mm</td>
            <td className={labelCls}>REPETICION INDIVIDUAL:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.individualRepetition ?? ''} disabled={disabled}
                onChange={e => handleF(e.target.value)} />
            </td>
            <td className={unitCls}>mm</td>
          </tr>
          {/* Row B | G */}
          <tr>
            <td className={labelCls}>BOBINAS AL EJE:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.bobinCount ?? ''} disabled={disabled}
                onChange={e => handleB(e.target.value)} />
            </td>
            <td className={unitCls}>Pza</td>
            <td className={labelCls}>No. REPETICIONES:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.repetitionCount ?? ''} disabled={disabled}
                onChange={e => handleG(e.target.value)} />
            </td>
            <td className={unitCls}></td>
          </tr>
          {/* Row C (calc) | H — CIRCUNFERENCIA DE MANGA */}
          <tr>
            <td className={labelCls}>ANCHO DE IMPRESIÓN:</td>
            <td className={computedCls}>{printWidth || (s.printWidth || '—')}</td>
            <td className={unitCls}>mm</td>
            <td className={labelCls}>CIRCUNFERENCIA DE MANGA:</td>
            <td className={computedCls}>{mangaCircComputed || (s.cylinderCircumference || '—')}</td>
            <td className={unitCls}>mm</td>
          </tr>
          {/* Row D | I */}
          <tr>
            <td className={labelCls}>ANCHO DE REFILE:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.reelWidth ?? ''} disabled={disabled}
                onChange={e => handleD(e.target.value)} />
            </td>
            <td className={unitCls}>mm</td>
            <td className={labelCls}>MANGAS A USAR:</td>
            <td className={valueCls}>
              <input
                type="number"
                min="0"
                max={s.selectedMangaCantidad ? parseInt(s.selectedMangaCantidad) : 8}
                className={inputCls}
                value={s.cylinderCount ?? ''}
                disabled={disabled}
                onChange={e => onChange('cylinderCount', e.target.value)}
              />
            </td>
            <td className={unitCls + ' text-blue-500'}>
              {s.selectedMangaCantidad ? `/${s.selectedMangaCantidad}` : ''}
            </td>
          </tr>
          {/* Row: Ancho del Corte P/Bolseo — free text, unless Stand Pouch drives it */}
          <tr>
            <td className={labelCls}>ANCHO DEL CORTE P/BOLSEO:</td>
            {standPouchEnabled ? (
              <td className={computedCls}>{baggingCutWidth || '—'}</td>
            ) : (
              <td className={valueCls}>
                <input type="text" className={inputCls} value={s.baggingCutWidth ?? ''} disabled={disabled}
                  onChange={e => onChange('baggingCutWidth', e.target.value)} />
              </td>
            )}
            <td className={unitCls}>mm</td>
            <td className="border bg-gray-50"></td>
            <td className="border"></td>
            <td className="border"></td>
          </tr>
          {/* ROT-214: Stand Pouch field moved to its own row, directly below
              Ancho del Corte P/Bolseo, instead of sharing that row. */}
          {standPouchEnabled && (
            <tr>
              <td className={labelCls}>REFILE P/BOLSA STAND POUCH:</td>
              <td className={valueCls}>
                <input type="number" className={inputCls} value={s.refileStandPouch ?? ''} disabled={disabled}
                  onChange={e => handleRefileStandPouch(e.target.value)} />
              </td>
              <td className={unitCls}>mm</td>
              <td className="border bg-gray-50"></td>
              <td className="border"></td>
              <td className="border"></td>
            </tr>
          )}
          {/* Row E (calc) | J */}
          <tr>
            <td className={labelCls}>ANCHO DEL SUSTRATO:</td>
            <td className={computedCls}>{substrateWidth || (s.substrateWidth || '—')}</td>
            <td className={unitCls}>mm</td>
            <td className={labelCls}>LINEA DE CORTE:</td>
            <td className={valueCls + ' text-center'}>
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={!!s.cutLine} disabled={disabled}
                onChange={e => onChange('cutLine', e.target.checked)} />
            </td>
            <td className={unitCls}>SI</td>
          </tr>
          {/* Row (empty) | K */}
          <tr>
            <td className="border bg-gray-50"></td>
            <td className="border"></td>
            <td className="border"></td>
            <td className={labelCls}>PIE DE CASA:</td>
            <td className={valueCls + ' text-center'}>
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={!!s.footHouse} disabled={disabled}
                onChange={e => onChange('footHouse', e.target.checked)} />
            </td>
            <td className={unitCls}>NO</td>
          </tr>
          {/* Row L | M */}
          <tr>
            <td className={labelCls}>FOTOCELDA ANCHO:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.photocell?.width ?? ''} disabled={disabled}
                onChange={e => onChange('photocell.width', e.target.value)} />
            </td>
            <td className={unitCls}>mm</td>
            <td className={labelCls}>FOTOCELDA LARGO:</td>
            <td className={valueCls}>
              <input type="number" className={inputCls} value={s.photocell?.height ?? ''} disabled={disabled}
                onChange={e => onChange('photocell.height', e.target.value)} />
            </td>
            <td className={unitCls}>mm</td>
          </tr>
          {/* Row REPETICION CONTINUA */}
          <tr>
            <td className="border px-3 py-2 font-bold text-orange-600 bg-orange-50 text-xs" colSpan={2}>
              REPETICION CONTINUA
            </td>
            <td className="border px-2 py-2 text-center">
              <input type="checkbox" className="h-4 w-4 accent-orange-500" checked={!!s.continuousRepetition} disabled={disabled}
                onChange={e => onChange('continuousRepetition', e.target.checked)} />
            </td>
            <td className="border px-2 py-2 text-gray-400 text-xs" colSpan={3}>SI/NO</td>
          </tr>
        </tbody>
      </table>

      {/* Colors Table */}
      <div className="mt-4">
        <p className="text-xs text-gray-500 italic mb-2">
          Nota: Dependiendo del Numero de Mangas son las celdas de colores que se deben habilitar para captura
        </p>
        <table className="w-full border border-gray-300 text-sm">
          <thead>
            <tr>
              <th className="border px-2 py-2 bg-gray-50 text-xs text-gray-600 w-8">#</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Color Base</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Pantone</th>
              <th className="border px-2 py-2 bg-gray-50 text-xs text-gray-600 w-8">#</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Color Base</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Pantone</th>
              <th className="border px-2 py-2 bg-gray-50 text-xs text-gray-600 w-8">#</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Color Base</th>
              <th className="border px-3 py-2 bg-gray-50 text-xs text-gray-700 font-medium">Pantone</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map(row => (
              <tr key={row}>
                {[0, 3, 6].map((offset, colIdx) => {
                  const idx = row + offset;
                  if (idx >= 9) return <td key={colIdx} className="border" colSpan={3}></td>;
                  const isEnabled = idx < mangaCount;
                  const cr = colorRows[idx];
                  return (
                    <React.Fragment key={colIdx}>
                      <td className="border px-2 py-1 text-center text-gray-500 text-xs font-medium bg-gray-50">{idx + 1}</td>
                      <td className="border px-1 py-1">
                        <select
                          value={cr.colorBase}
                          onChange={e => handleColorRow(idx, 'colorBase', e.target.value)}
                          disabled={disabled || !isEnabled}
                          className={`w-full border border-gray-300 rounded px-1 py-1 text-xs focus:outline-none ${!isEnabled ? 'bg-gray-100 text-gray-400' : 'bg-white'}`}
                        >
                          <option value="">-- Seleccionar --</option>
                          {colorOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </td>
                      <td className="border px-1 py-1">
                        <input
                          type="text"
                          value={cr.pantone}
                          onChange={e => handleColorRow(idx, 'pantone', e.target.value)}
                          disabled={disabled || !isEnabled}
                          placeholder={isEnabled ? 'Captura' : ''}
                          className={`w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none ${!isEnabled ? 'bg-gray-100 text-gray-400' : ''}`}
                        />
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProcessSpecsTableForm;
